/* eslint-disable complexity */
/**
 * @file 任务列表store
 * @author sunyingying01
 */

import dayjs from 'dayjs';
import {action, computed, flow, makeObservable, observable} from 'mobx';
import {handleRequestError} from '@/utils/errorHandler';
import {TaskStep} from '@/dicts/taskRuning';
import {
    createJob,
    deleteJob,
    updateJob,
    getJobDetail,
    startJob,
    putSaveEditJob,
    postRecovery,
    copyJob,
    checkStatusJob
} from '@/api/console';
import {transformToAlgorithmTree} from '@/utils/index';
import {getAutoTerminationState} from '@/utils/job/autoTermination';
import StepStore from './StepStore';
import {AlgorithmStore} from './AlgorithmStore';
import {EvaluatorStore} from './EvaluatorStore';
import {DescParameterStore} from './DescParameterStore';

export class JobStore {
    page: string = '';
    /** 任务Id * */
    job_id: string = '';
    /** 任务名称* */
    name: string = `job_${dayjs().format('YYYYMMDDHHmmssSSS')}`;
    /** 任务来源 */
    sourceType: JobSourceType | '' = '';
    /** 区分混合云和web端 混合云是任务类型 可以通过config.yaml文件修改 */
    cloudType: CloudType | '' = '';
    /** 任务进度 (0-100) */
    percentage: number = 0;
    /** 当前迭代次数 */
    iteration: number = 0;
    /** 总迭代次数 */
    iterationSum: number = 0;
    /** 计费信息 */
    charge_info: any = {};
    /** 运行时长 * */
    runTime: number = 0;
    /** 任务状态 */
    status: TASK_STRATEGIES_TYPES = 'DRAFT';
    /** 任务阶段 */
    stage: STAGE_TYPE = TaskStage.COLDSTART;
    /** 任务对应的阶段状态 */
    stage_status: TASK_STRATEGIES_TYPES | '' = '';
    /** 步骤组件 */
    stepStore: StepStore;
    /** 任务初始算法 * */
    algorithmStore: AlgorithmStore;
    /** 任务评估算法 * */
    evaluatorStore: EvaluatorStore;
    /** 任务描述范围详情配置 */
    descParameterStore: DescParameterStore;
    /** 是否展示创建弹窗 */
    showEditJobModal: boolean = false;
    /** 当前编辑弹窗状态 */
    editModalStatus: 'create' | 'update' = 'create';
    /** 如果update_status存在，值等于UPDATE_CHECKING 任务重新配置可行性判断界面 */
    update_status: 'UPDATE_CHECKING' | '' = '';
    /** 创建按钮loading */
    createLoading: boolean = false;
    /** 开启任务按钮loading */
    startLoading: boolean = false;
    /** 详情loading */
    detailsLoading: boolean = false;
    /** 复制任务loading */
    copyLoading: boolean = false;

    /** 运行界面开启任务 */
    isConfigStartTask: boolean = false;

    /** 任务开启错误信息 */
    startJobErrorMsg: string = '';

    /** 自动保存定时器 */
    autoSaveTimer?: NodeJS.Timeout | null;
    /** 是否正在保存中 */
    saving: boolean = false;
    /** 最后保存时间 */
    lastSavedTime: string = '';

    /** 初始任务详情快照 */
    initialSnapshot: any = null;

    // 续跑相关参数
    /** 选中的推荐解ID列表 */
    select_solutions: string[] = [];
    /** 选中所有节点的信息 */
    select_solutions_info: InitialSolutionFileInfo[] = [];
    // 初次演化：0；续跑：1，默认为：0
    evolve_mod: number = 0;

    // webIde拼接的前缀
    web_ide_path: string = '';

    // 任务终止相关信息
    /** 任务是否被自动终止 */
    isAutoTerminated: boolean = false;
    /** 沙盒是否存在 不存在：tasks/recovery POST */
    sandboxExists: boolean = false;
    /** 已消耗资源点 */
    consumedCredit?: number | null;
    /** 已退回资源点 */
    refundedCredit?: number | null;
    /** 是否是示例任务 */
    isExampleJob: boolean = false;
    /** 本地标记：任务已删除，阻止旧实例继续请求详情 */
    isDeleted: boolean = false;

    update = action((params: Partial<JobStore>) => {
        Object.assign(this, params);
    });

    constructor(job_id: string = '') {
        this.job_id = job_id;
        this.stepStore = new StepStore(job_id);
        this.algorithmStore = new AlgorithmStore(job_id);
        this.evaluatorStore = new EvaluatorStore(job_id);
        this.descParameterStore = new DescParameterStore(job_id);

        makeObservable(this, {
            job_id: observable,
            page: observable,
            name: observable,
            percentage: observable,
            iteration: observable,
            iterationSum: observable,
            status: observable,
            stage: observable,
            algorithmStore: observable,
            evaluatorStore: observable,
            descParameterStore: observable,
            editModalStatus: observable,
            showEditJobModal: observable,
            createLoading: observable,
            startJobErrorMsg: observable,
            lastSavedTime: observable,
            detailsLoading: observable,
            isConfigStartTask: observable,
            stage_status: observable,
            update_status: observable,
            startLoading: observable,
            copyLoading: observable,
            select_solutions: observable,
            stepStore: observable,
            saving: observable,
            sourceType: observable,
            cloudType: observable,
            evolve_mod: observable,
            select_solutions_info: observable,
            web_ide_path: observable,
            isAutoTerminated: observable,
            consumedCredit: observable,
            sandboxExists: observable,
            isExampleJob: observable,
            isDeleted: observable,
            updateEditJobModalStatus: action,
            updateShowEditModal: action,
            stopAutoSave: action.bound,
            startAutoSave: action.bound,
            restoreParamFormBackend: action.bound,
            setLastSavedTime: action.bound,
            initSetEditModal: action.bound,
            setDetailsLoading: action.bound,
            setCreateLoading: action.bound,
            setConfigStartTask: action.bound,
            resetSnapshots: action.bound,
            updateConfigStatus: action.bound,
            setSelectSolutions: action.bound,
            updadeName: action.bound,
            fetchGreatJob: flow.bound,
            fetchUpdateJob: flow.bound,
            fetchJobDetail: flow.bound,
            fetchSaveUpdateJob: flow.bound,
            fetchStartTask: flow.bound,
            fetchDeleteJob: flow.bound,
            fetchPostRecovery: flow.bound,
            fetchCopyJob: flow.bound,
            fetchCheckStatusJob: flow.bound,
            isShowInitialingPage: computed,
            isDRAFT: computed,
            enableColdStart: computed,
            iterationProgress: computed,
            taskDisabledState: computed,
            hasChanged: computed,
            hasUpdateStatus: computed,
            isRecovery: computed,
            isCliJob: computed,
            isHybridJob: computed,
            isSpecialPlatform: computed
        });
    }

    /** 是否是CLI任务 */
    get isCliJob() {
        return ['famou-python-sdk', 'cli'].includes(this.sourceType);
    }

    /** 是否是CLI任务 */
    get isHybridJob() {
        return ['hybrid'].includes(this.cloudType);
    }

    /** 是否是其他平台创建 */
    get isSpecialPlatform() {
        if (this.isCliJob || this.isHybridJob) {
            return true;
        }
        return false;
    }

    /** 是否重新配置，在可行性判断 */
    get hasUpdateStatus() {
        return this.update_status === 'UPDATE_CHECKING';
    }

    /**
     * 只监听 algorithmStore 和 descParameterStore 的变化
     * 监听评估算法是否为空为空
    */
    get hasChanged() {
        if (this.algorithmStore.isEmptyStarEvaluatorAlgorithm) {
            return false;
        }
        return (
            this.evaluatorStore.hasChanged
                || this.descParameterStore.hasChanged
        );
    }

    /**
     * 任务展示状态是否已经开启
     * 任务status是草稿，为空 false 不展示running 界面
     *  */
    get isShowInitialingPage(): boolean {
        if (['DRAFT'].includes(this.stage_status)
                || ['DRAFT'].includes(this.status)
                || ['CREATED', 'INITIALING'].includes(this.page)) {
            return true;
        }

        /** 阶段状态是校验阶段 */
        if (!this.stage_status && !this.status) {
            return true;
        }

        return false;
    }

    /** 任务展示状态是否被终止 */
    get isShowTermination(): boolean {
        return ['FAILED', 'CANCELLED'].includes(this.status);
    }

    /** 是否是冷启动任务 */
    get enableColdStart(): boolean {
        return this.descParameterStore.evolveConfig?.enable_cold_start;
    }

    /** 任务迭代进度（百分比） */
    get iterationProgress(): number {
        if (this.iterationSum <= 0) {
            return 0;
        }
        return (this.iteration / this.iterationSum) * 100;
    }

    /** start 开启任务按钮禁用状态 */
    get taskDisabledState() {
        if (!this.descParameterStore.prompt.task) {
            this.stepStore.markCompleted(1, false);
            return true;
        }

        this.stepStore.markCompleted(1, true);
        return false;
    }

    /** 是否是草稿状态 */
    get isDRAFT(): boolean {
        return ['DRAFT'].includes(this.stage_status) || ['DRAFT'].includes(this.status);
    }

    /** 任务是否可以恢复任务 */
    get isRecovery(): boolean {
        return ['FAILED', 'CANCELLED', 'STOPPED', 'COMPLETED', 'TERMINATED'].includes(this.status);
    }

    /** 更新任务名称 * */
    updadeName(name: string) {
        this.name = name;
    }

    updateConfigStatus() {
        this.update_status === '';
    }

    /** 在任务详情拉取或更新成功后调用，用于重置快照 */
    resetSnapshots() {
        this.evaluatorStore.resetSnapshot();
        this.descParameterStore.resetSnapshot();
    }

    setSelectSolutions(select_solutions: string[]) {
        this.select_solutions = select_solutions;
    }

    /** 创建任务 */
    * fetchGreatJob(this: JobStore) {
        try {
            this.createLoading = true;
            const res: ApiResponse = yield createJob({name: this.name});
            this.job_id = res.data?.job_id;
            this.initSetEditModal();
            return this.job_id;
        }
        catch (error) {
            throw error;
        }
        finally {
            this.createLoading = false;
        }
    }


    /** 任务容器释放后恢复任务 容器恢复 */
    * fetchPostRecovery(this: JobStore, job_id: string = '') {
        if (!job_id && !this.job_id) {
            return;
        }
        yield postRecovery(job_id || this.job_id);
    }

    /** 在任务运行后，更新任务信息 * */
    * fetchUpdateJob(this: JobStore, params?: Partial<FetchGreatJobParams>) {
        try {
            this.createLoading = true;
            const data: Partial<FetchGreatJobParams> = {
                job_id: this.job_id,
                star_evaluator: (this.evaluatorStore.star_evaluator as FileInfo)?.file_name,
                prompts: this.descParameterStore.prompt,
                evolve_config: this.descParameterStore.evolveConfig,
                ...params
            };
            const res: ApiResponse = yield updateJob(data);
            this.job_id = res.data?.job_id;
            // 更新成功后重置快照
            this.resetSnapshots();
        }
        finally {
            this.createLoading = false;
        }
    }

    /** 删除任务 */
    * fetchDeleteJob(this: JobStore) {
        try {
            this.createLoading = true;
            yield deleteJob(this.job_id);
            this.isDeleted = true;
            this.stopAutoSave();
            this.initSetEditModal();
        }
        catch (error) {
            throw error;
        }
        finally {
            this.createLoading = false;
        }
    }

    /** 复制任务 */
    * fetchCopyJob(this: JobStore, copy_job_id: string) {
        try {
            this.copyLoading = true;
            yield copyJob(copy_job_id);
        }
        catch (error) {
            throw error;
        }
        finally {
            this.copyLoading = false;
        }
    }

    /** 获取任务详情 * */
    * fetchJobDetail(this: JobStore) {
        const requestJobId = this.job_id;
        try {
            if (!requestJobId || this.isDeleted) {
                return this;
            }

            const res: ApiResponse = yield getJobDetail(requestJobId);
            if (requestJobId !== this.job_id || this.isDeleted) {
                return this;
            }

            if (res.data) {
                this.update_status = res.data?.update_status || '';
                this.restoreParamFormBackend(res.data);
                this.resetSnapshots(); // 拉取详情后重置快照
            }
            return this;
        }
        catch (error) {
            throw error;
        }
        finally {
            this.detailsLoading = false;
        }
    }

    /** 任务编辑定时save */
    * fetchSaveUpdateJob(this: JobStore, params?: FetchSaveUpdateJobParams, tipError: boolean = true) {
        try {
            const defaultParams: Partial<FetchGreatJobParams> = {
                job_id: this.job_id,
                step: String(this.stepStore.apiStep)
            };

            const job: Partial<FetchGreatJobParams> = {
                prompts: this.descParameterStore.prompt,
                evolve_config: this.descParameterStore.evolveConfig
            };

            // 从 params 中取出 reqdetails，默认 true
            const {reqdetails = true, ...rest} = params ?? job as FetchSaveUpdateJobParams;
            yield putSaveEditJob({...defaultParams, ...rest});
            if (reqdetails && (this.job_id || params?.job_id)) {
                this.detailsLoading = true;
                yield this.fetchJobDetail();
            }
            this.stopAutoSave();
            return this;
        } catch (error) {
            if (tipError) {
                handleRequestError({error});
            }
            throw error;
        }
    }

    /** 任务初始化开启任务 */
    * fetchStartTask(this: JobStore, page = '') {
        try {
            this.startJobErrorMsg = '';
            this.startLoading = true;
            const res: ApiResponse = yield startJob(this.job_id);

            if (String(res.code) === 'ErrInsufficientQuota') {
                const data = res.data || {};
                return {
                    insufficientQuota: true as const,
                    estimatedCost: data.requested ?? 0,
                    availableCredits: data.available_credits?.total ?? 0,
                    gap: data.shortfall ?? 0,
                    userLevel: data.subscription_plan ?? '',
                    rechargeUrl: data.recharge_url ?? {}
                };
            }

            this.job_id = res.data.job_id;
            this.status = res.data.job_status;
            if (['SELECT'].includes(page)) {
                return res.data;
            }
            this.stepStore.setcurrentStep(String(TaskStep.COLD_START));
            yield this.fetchSaveUpdateJob({step: String(TaskStep.COLD_START)});
        }
        finally {
            this.startLoading = false;
        }
    }

    /** 任务配置开启任务 */
    * fetchConfigStartTask(this: JobStore) {
        try {
            this.startJobErrorMsg = '';
            this.startLoading = true;
            yield startJob(this.job_id);
        }
        finally {
            this.startLoading = false;
        }
    }

    /** 任务复制提示信息状态修改 */
    * fetchCheckStatusJob(this: JobStore, params: {check_key: string, status: boolean}) {
        yield checkStatusJob(params);
    }

    /** TODO: 任务后端数据整理赋值前端格式 */
    restoreParamFormBackend(json: any): void {
        this.job_id = json.job_id;
        this.name = json.name || this.name;
        this.percentage = json.percentage ?? 0;
        this.iteration = Number(json.charge_info?.iteration ?? 0);
        this.iterationSum = Number(json.charge_info?.iteration_sum ?? 0);
        this.status = json.status || this.status;
        this.stage = json.stage;
        this.stage_status = json.stage_status;
        this.sourceType = json.source_type || '';
        this.cloudType = json.cloud_type || '';
        this.web_ide_path = json.web_ide_path || '';
        this.charge_info = json.charge_info || {};
        this.isExampleJob = json.is_example_job;

        // 任务终止相关信息
        const autoTerminationState = getAutoTerminationState(json);
        this.isAutoTerminated = autoTerminationState.isAutoTerminated;
        this.sandboxExists = autoTerminationState.sandboxExists;
        this.consumedCredit = autoTerminationState.consumedCredit;
        this.refundedCredit = autoTerminationState.refundedCredit;

        if (json.step) {
            this.stepStore.setcurrentStep(Number(json.step));
        }
        if ((json.initial_algorithm || json.star_evaluator) && json.initial_dir?.root) {
            this.algorithmStore.setAlgorithm(
                transformToAlgorithmTree(json.initial_dir, json.star_initial_algorithm),
                json.star_initial_algorithm,
                json.star_evaluator
            );
        }

        // 设置初始算法和评估器后，更新步骤完成状态
        if (json.star_evaluator && json.star_initial_algorithm) {
            this.stepStore.markCompleted(0);
        }

        this.descParameterStore.restoreDescParameterFormBackend(json);

        // 任务续跑获取详情
        if (json.continue_evolve_config) {
            const {select_solutions = [], select_solutions_info = ''} = json.continue_evolve_config;
            this.select_solutions = select_solutions || [];
            this.select_solutions_info = select_solutions_info ? JSON.parse(select_solutions_info) : [];
        }
    }

    /** 更新当前编辑弹窗状态 */
    updateEditJobModalStatus = (status: 'create' | 'update') => {
        this.editModalStatus = status;
    };

    /** 更新是否展示任务编辑弹窗可控状态 */
    updateShowEditModal = (status: boolean) => {
        this.showEditJobModal = status;
    };

    /** 关闭弹窗，初始化弹窗配置 */
    initSetEditModal() {
        this.showEditJobModal = false;
        this.editModalStatus = 'create';
    }

    setCreateLoading(val: boolean) {
        this.createLoading = val;
    }

    setDetailsLoading(val: boolean) {
        this.detailsLoading = val;
    }

    setLastSavedTime() {
        this.lastSavedTime = new Date().toLocaleTimeString(
            [], {hour: '2-digit', minute: '2-digit', second: '2-digit'}
        );
    }
    setConfigStartTask(val: boolean) {
        this.isConfigStartTask = val;
    }
    /** 启动自动保存 */
    startAutoSave() {
        if (this.autoSaveTimer) {
            this.stopAutoSave();
        }
        this.autoSaveTimer = setInterval(async () => {
            if (this.job_id && !this.saving) {
                try {
                    this.saving = true;
                    await this.fetchSaveUpdateJob();
                    this.setLastSavedTime();
                } catch (error) {
                    // 保存失败时停止自动保存，避免持续调用失败的接口
                    this.stopAutoSave();
                } finally {
                    this.saving = false;
                }
            }
        }, 5000); // 5秒自动保存一次
    }

    /** 停止自动保存 */
    stopAutoSave() {
        if (this.autoSaveTimer) {
            clearInterval(this.autoSaveTimer);
            this.autoSaveTimer = null;
        }
    }
}


// 前端调用参数：在接口参数的基础上，扩展一个本地用的 reqdetails
type FetchSaveUpdateJobParams = Partial<FetchGreatJobParams> & {
    /** 是否需要获取详情，默认 true，不传给后端 */
    reqdetails?: boolean;
    continue_evolve_config?: {
        select_solutions_info: string;
        select_solutions: Array<string | undefined>;
        evolve_config?: string | object;
        evolve_mod?: number;
        select_excellent_solutions?: number;
        name?: string;
    };

};
